import { FeedbackService } from 'src/app/core/services/feedback.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { RecordingStorageService } from '../../../core/services/recording-storage.service';
import { RecordingDownloadService, DownloadLogEntry } from '../../../core/services/recording-download.service';
import { RecordingRetryService, RetryStatusUpdate } from '../../../core/services/recording-retry.service';
import { PatientNoteService } from '../../../core/services/patient-note.service';
import { NoteTypeService } from '../../../core/services/note-type.service';
import { PatientAiService } from '../../../core/services/patient-ai.service';
import { FailedRecording } from '../../../core/interfaces/recording-storage.interface';

/** The label, icon and tone shown against one row. */
interface RecordingOutcome {
  label: string;
  detail: string;
  icon: string;
  tone: 'unfinished' | 'failed' | 'ready';
}

const RECORDING_OUTCOMES: Record<'unfinished' | 'failed' | 'ready', RecordingOutcome> = {
  unfinished: {
    label: 'Unfinished',
    detail: 'This consultation was recorded but never processed. The audio is safe — process it to generate the notes.',
    icon: 'hourglass_empty',
    tone: 'unfinished',
  },
  failed: {
    label: 'Needs retry',
    detail: 'Part of this recording did not upload or transcribe. Retrying will pick up where it stopped.',
    icon: 'error_outline',
    tone: 'failed',
  },
  ready: {
    label: 'Notes ready',
    detail: 'Processing finished. View the generated notes.',
    icon: 'check_circle',
    tone: 'ready',
  },
};

interface DownloadError {
  recordingId: string;
  timestamp: string;
  error: any;
  stackTrace: string;
  errorDetails: string;
}


@Component({
  selector: 'app-failed-recordings-dialog',
  templateUrl: './failed-recordings-dialog.component.html',
  styleUrls: ['./failed-recordings-dialog.component.scss']
})
export class FailedRecordingsDialogComponent implements OnInit, OnDestroy {

  SHOW_LOGS = false;

  failedRecordings: FailedRecording[] = [];
  recordingSizes: Map<string, string> = new Map(); // Cache file sizes

  storageUsed: number = 0;
  storageTotal: number = 0;
  storageUsagePercent: number = 0;
  storageWarning: boolean = false;
  storageColor: 'primary' | 'warn' = 'primary';

  // Download error tracking (for backward compatibility)
  downloadErrors: Map<string, DownloadError> = new Map();

  // Download log tracking (for all downloads - success and failure)
  downloadLogs: Map<string, DownloadLogEntry[]> = new Map();
  expandedLogs: Set<string> = new Set();
  copySuccess: Map<string, boolean> = new Map();

  // Retry tracking
  retryingRecordings: Set<string> = new Set(); // Track which recordings are being retried
  retryStatus: Map<string, RetryStatusUpdate> = new Map(); // Status updates for each retry
  retryResults: Map<string, any> = new Map(); // Store final results for View button
  
  // Computed properties for template (updated when data changes)
  retryingRecordingIds: string[] = []; // Array for *ngFor
  recordingComputedData: Map<string, {
    hasFinalResult: boolean;
    isRetrying: boolean;
    retryStatus?: RetryStatusUpdate;
    patientName: string; // Pre-computed patient name for status display
    outcome: RecordingOutcome; // What actually happened, for the per-row label
  }> = new Map();

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<FailedRecordingsDialogComponent>,
    private recordingStorageService: RecordingStorageService,
    private recordingDownloadService: RecordingDownloadService,
    private recordingRetryService: RecordingRetryService,
    private feedbackService: FeedbackService,
    private router: Router,
    private patientNoteService: PatientNoteService,
    private noteTypeService: NoteTypeService,
    private patientAiService: PatientAiService
  ) { }

  async ngOnInit() {
    await this.loadFailedRecordings();
    await this.loadStorageUsage();
    this.updateComputedData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadFailedRecordings() {
    try {
      // One shared query, in the service. This used to be reimplemented here line for line.
      const allRecordings = await this.recordingStorageService.getAllUnprocessedRecordings();
      
      // Merge retry results back into recordings to ensure they stay visible
      // This handles cases where the recording might not have finalResult in storage yet
      const recordingsWithRetryResults = allRecordings.map(recording => {
        if (this.retryResults.has(recording.id)) {
          // If we have a retry result but recording doesn't have finalResult, add it
          if (!recording.finalResult) {
            recording.finalResult = this.retryResults.get(recording.id);
            recording.status = 'completed'; // Ensure it's marked as completed
          }
        }
        return recording;
      });
      
      // Also check if any recordings in retryResults are missing from loaded recordings
      // This can happen if the recording was filtered out due to timing issues
      const missingRecordingIds = Array.from(this.retryResults.keys()).filter(
        id => !recordingsWithRetryResults.find(r => r.id === id)
      );
      
      // Try to get missing recordings from storage
      const missingRecordingsPromises = missingRecordingIds.map(async (recordingId) => {
        try {
          const recording = await this.recordingStorageService.getRecording(recordingId);
          if (recording) {
            recording.finalResult = this.retryResults.get(recordingId);
            recording.status = 'completed';
            return recording;
          }
        } catch (error) {
          // Recording might not exist anymore, ignore
        }
        return null;
      });
      
      const missingRecordings = (await Promise.all(missingRecordingsPromises)).filter(
        (r): r is FailedRecording => r !== null
      );
      
      // Final deduplication: combine all recordings and remove duplicates by id
      const finalRecordingsMap = new Map<string, FailedRecording>();
      for (const recording of recordingsWithRetryResults) {
        finalRecordingsMap.set(recording.id, recording);
      }
      for (const recording of missingRecordings) {
        // Only add if not already present
        if (!finalRecordingsMap.has(recording.id)) {
          finalRecordingsMap.set(recording.id, recording);
        }
      }
      
      this.failedRecordings = Array.from(finalRecordingsMap.values());
      
      // Load file sizes for all recordings
      await this.loadRecordingSizes();
      this.updateComputedData();
    } catch (error) {
      console.error('Error loading failed recordings:', error);
      this.feedbackService.showToast('Error loading failed recordings');
    }
  }

  async loadRecordingSizes() {
    this.recordingSizes.clear();
    for (const recording of this.failedRecordings) {
      try {
        const size = await this.getRecordingFileSize(recording);
        this.recordingSizes.set(recording.id, size);
      } catch (error) {
        console.warn(`Failed to load size for recording ${recording.id}:`, error);
        this.recordingSizes.set(recording.id, 'N/A');
      }
    }
  }

  async loadStorageUsage() {
    try {
      const usage = await this.recordingStorageService.getStorageUsage();
      // getStorageUsage() already returns values in MB, so use them directly
      this.storageUsed = usage.used || 0;
      this.storageTotal = usage.quota || 0;
      // ✅ Use the percentage calculated by the service (based on our recordings, not total browser usage)
      this.storageUsagePercent = usage.usagePercent || 0;
      this.storageWarning = this.storageUsagePercent > 80;
      this.storageColor = this.storageUsagePercent > 80 ? 'warn' : 'primary';
    } catch (error) {
      console.error('Error loading storage usage:', error);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  async getRecordingFileSize(recording: FailedRecording): Promise<string> {
    try {
      // Metadata only - loading the audio just to sum sizes is what used to blow up the
      // IndexedDB connection here.
      const segments = await this.recordingStorageService.getRecordingSegmentMetadata(recording.id);
      const totalSize = segments.reduce((sum, segment) => sum + (segment.size || 0), 0);
      return this.formatFileSize(totalSize);
    } catch (error) {
      console.warn('Failed to get recording file size:', error);
      return 'N/A';
    }
  }

  async downloadRecording(recording: FailedRecording) {
    // Clear any previous logs and errors for this recording
    this.downloadLogs.delete(recording.id);
    this.downloadErrors.delete(recording.id);
    this.expandedLogs.delete(recording.id);

    // Track logs for this download
    const logs: DownloadLogEntry[] = [];

    try {
      // Download with log callback
      await this.recordingDownloadService.downloadRecording(recording.id, (entry: DownloadLogEntry) => {
        logs.push(entry);
      });

      // Store logs
      this.downloadLogs.set(recording.id, logs);

      // Auto-expand the log section
      this.expandedLogs.add(recording.id);

      this.feedbackService.showToast('Recording downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading recording:', error);
      this.feedbackService.showToast('Error downloading recording');

      // Store logs (will include error logs from service)
      this.downloadLogs.set(recording.id, logs);

      // Capture full error details with stack trace for error section
      const errorDetails = this.formatErrorDetails(error, recording);
      const stackTrace = this.getStackTrace(error);

      this.downloadErrors.set(recording.id, {
        recordingId: recording.id,
        timestamp: new Date().toISOString(),
        error: error,
        stackTrace: stackTrace,
        errorDetails: errorDetails
      });

      // Auto-expand the log section
      this.expandedLogs.add(recording.id);
    }
  }

  private formatErrorDetails(error: any, recording: FailedRecording): string {
    let details = `Download Error for Recording: ${recording.id}\n`;
    details += `Patient: ${recording.patientName || 'Unknown'}\n`;
    details += `Date: ${recording.createdAt}\n`;
    details += `Timestamp: ${new Date().toISOString()}\n\n`;

    details += `Error Message: ${error?.message || String(error)}\n`;

    if (error?.name) {
      details += `Error Name: ${error.name}\n`;
    }

    if (error?.errorCode) {
      details += `Error Code: ${error.errorCode}\n`;
    }

    if (error?.status) {
      details += `HTTP Status: ${error.status}\n`;
    }

    if (error?.statusText) {
      details += `HTTP Status Text: ${error.statusText}\n`;
    }

    // Include original error if wrapped
    if (error?.originalError) {
      details += `\nOriginal Error:\n`;
      details += `  Message: ${error.originalError?.message || String(error.originalError)}\n`;
      if (error.originalError?.name) {
        details += `  Name: ${error.originalError.name}\n`;
      }
      if (error.originalError?.stack) {
        details += `  Stack:\n${error.originalError.stack.split('\n').map((line: string) => `    ${line}`).join('\n')}\n`;
      }
    }

    details += `\nStack Trace:\n${this.getStackTrace(error)}\n`;

    // Add environment details
    details += `\nEnvironment Details:\n`;
    details += `User Agent: ${navigator.userAgent}\n`;
    details += `Platform: ${navigator.platform}\n`;
    details += `Language: ${navigator.language}\n`;

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    details += `iOS Device: ${isIOS ? 'Yes' : 'No'}\n`;

    // iOS-specific information
    if (isIOS) {
      details += `\niOS-Specific Notes:\n`;
      details += `  iOS Safari may block programmatic downloads.\n`;
      details += `  User interaction may be required for downloads.\n`;
      details += `  Try long-pressing the download link.\n`;
    }

    if ((window as any).deviceMemory) {
      details += `Device Memory: ${(window as any).deviceMemory}GB\n`;
    }

    return details;
  }

  private getStackTrace(error: any): string {
    if (error?.stack) {
      return error.stack;
    }

    if (error?.error?.stack) {
      return error.error.stack;
    }

    // Try to generate a stack trace
    try {
      throw new Error('Stack trace generation');
    } catch (e: any) {
      if (e.stack) {
        // Remove the first two lines (our throw and this function)
        const lines = e.stack.split('\n');
        return lines.slice(2).join('\n');
      }
    }

    return 'Stack trace not available';
  }

  toggleLogExpansion(recordingId: string): void {
    if (this.expandedLogs.has(recordingId)) {
      this.expandedLogs.delete(recordingId);
    } else {
      this.expandedLogs.add(recordingId);
    }
  }

  toggleErrorExpansion(recordingId: string): void {
    // Keep for backward compatibility if needed
  }

  copyLogToClipboard(recordingId: string): void {
    const logs = this.downloadLogs.get(recordingId);
    if (!logs || logs.length === 0) {
      return;
    }

    const logText = this.formatLogsForClipboard(logs, recordingId);

    navigator.clipboard.writeText(logText).then(() => {
      this.copySuccess.set(recordingId, true);
      this.feedbackService.showToast('Download log copied to clipboard');
      setTimeout(() => {
        this.copySuccess.delete(recordingId);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy log to clipboard:', err);
      this.feedbackService.showToast('Failed to copy to clipboard');
    });
  }

  copyErrorToClipboard(recordingId: string): void {
    const error = this.downloadErrors.get(recordingId);
    if (!error) {
      return;
    }

    navigator.clipboard.writeText(error.errorDetails).then(() => {
      this.copySuccess.set(recordingId, true);
      this.feedbackService.showToast('Error details copied to clipboard');
      setTimeout(() => {
        this.copySuccess.delete(recordingId);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy error to clipboard:', err);
      this.feedbackService.showToast('Failed to copy to clipboard');
    });
  }

  hasDownloadLogs(recordingId: string): boolean {
    const logs = this.downloadLogs.get(recordingId);
    return logs !== undefined && logs.length > 0;
  }

  hasDownloadError(recordingId: string): boolean {
    return this.downloadErrors.has(recordingId);
  }

  isLogExpanded(recordingId: string): boolean {
    return this.expandedLogs.has(recordingId);
  }

  isErrorExpanded(recordingId: string): boolean {
    // Keep for backward compatibility if needed
    return false;
  }

  getDownloadLogs(recordingId: string): DownloadLogEntry[] {
    return this.downloadLogs.get(recordingId) || [];
  }

  hasLogDetails(entry: DownloadLogEntry): boolean {
    return !!(entry.details && Object.keys(entry.details).length > 0);
  }

  formatLogDetails(details: any): string {
    if (!details || Object.keys(details).length === 0) {
      return '';
    }
    return JSON.stringify(details, null, 2);
  }

  formatLogsForClipboard(logs: DownloadLogEntry[], recordingId: string): string {
    const recording = this.failedRecordings.find(r => r.id === recordingId);
    let text = `Download Process Log for Recording: ${recordingId}\n`;
    text += `Patient: ${recording?.patientName || 'Unknown'}\n`;
    text += `Date: ${recording?.createdAt || 'N/A'}\n`;
    text += `Timestamp: ${new Date().toISOString()}\n\n`;

    text += `Environment Details:\n`;
    text += `User Agent: ${navigator.userAgent}\n`;
    text += `Platform: ${navigator.platform}\n`;
    text += `Language: ${navigator.language}\n`;

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    text += `iOS Device: ${isIOS ? 'Yes' : 'No'}\n\n`;

    text += `Process Log:\n`;
    text += `${'='.repeat(80)}\n\n`;

    logs.forEach((entry, index) => {
      const timestamp = new Date(entry.timestamp).toISOString();
      text += `[${index + 1}] ${timestamp} [${entry.level.toUpperCase()}] ${entry.message}\n`;

      if (entry.details && Object.keys(entry.details).length > 0) {
        text += `  Details: ${JSON.stringify(entry.details, null, 2)}\n`;
      }

      if (entry.stackTrace) {
        text += `  Stack Trace:\n${entry.stackTrace.split('\n').map(line => `    ${line}`).join('\n')}\n`;
      }

      text += `\n`;
    });

    return text;
  }

  async deleteRecording(recording: FailedRecording) {
    const confirmed = await this.feedbackService.showConfirmation('Confirm Deletion', `Are you sure you want to delete the recording for ${recording.patientName || 'Unknown Patient'}? This cannot be undone and this recording will be lost forever if you have not downloaded it.`, [], 'Cancel', 'Delete');
    if (!confirmed) {
      return;
    }

    try {
      await this.recordingStorageService.deleteRecording(recording.id);
      // Remove from size cache
      this.recordingSizes.delete(recording.id);
      this.feedbackService.showToast('Recording deleted');
      await this.loadFailedRecordings();
      // ✅ Recalculate storage usage after deletion
      await this.loadStorageUsage();
    } catch (error) {
      console.error('Error deleting recording:', error);
      this.feedbackService.showToast('Error deleting recording');
    }
  }

  // Retry functionality (following ai-dialog pattern)
  async retryRecording(recording: FailedRecording) {
    // Check if already retrying
    if (this.retryingRecordings.has(recording.id)) {
      this.feedbackService.showToast('Recording is already being retried');
      return;
    }

    // Check if another recording is being retried (only one at a time)
    if (this.retryingRecordings.size > 0) {
      this.feedbackService.showToast('Another recording is currently being retried. Please wait for it to complete.');
      return;
    }

    try {
      this.retryingRecordings.add(recording.id);
      this.retryStatus.set(recording.id, {
        recordingId: recording.id,
        status: 'Starting...',
        message: 'Initializing retry...'
      });
      this.updateComputedData();

      const result = await this.recordingRetryService.retryRecording(
        recording.id,
        recording.patientId,
        recording.groupId,
        recording.userId,
        recording.type,
        recording.startTime,
        recording.finishTime,
        (update: RetryStatusUpdate) => {
          // Status update callback
          this.retryStatus.set(update.recordingId, update);
          this.updateComputedData();
        }
      );

      this.retryingRecordings.delete(recording.id);
      this.updateComputedData();

      if (result.success) {
        this.feedbackService.showToast('Recording retry completed successfully');
        
        // Store final result and requestId for View button (matching ai-dialog pattern)
        if (result.finalResult) {
          this.retryResults.set(recording.id, result.finalResult);
          
          // Update the recording object in place to ensure it stays visible with View button
          const recordingIndex = this.failedRecordings.findIndex(r => r.id === recording.id);
          if (recordingIndex !== -1) {
            this.failedRecordings[recordingIndex].finalResult = result.finalResult;
            if (result.requestId) {
              this.failedRecordings[recordingIndex].requestId = result.requestId;
            }
            // Update status to completed so it shows View button
            this.failedRecordings[recordingIndex].status = 'completed';
          }
        }

        // Reload recordings to reflect updated status (but recording should still be visible if it has finalResult)
        await this.loadFailedRecordings();
        this.updateComputedData();
      } else {
        this.feedbackService.showToast(`Retry failed: ${result.error || 'Unknown error'}`);
        this.retryStatus.delete(recording.id);
        this.updateComputedData();
      }
    } catch (error: any) {
      console.error('Error retrying recording:', error);
      this.retryingRecordings.delete(recording.id);
      this.retryStatus.delete(recording.id);
      this.updateComputedData();
      this.feedbackService.showToast(`Error retrying recording: ${error.message || 'Unknown error'}`);
    }
  }


  // Check if recording is currently being retried (kept for backward compatibility, but prefer computed data)
  isRetrying(recordingId: string): boolean {
    return this.retryingRecordings.has(recordingId);
  }

  // Get retry status for a recording (kept for backward compatibility, but prefer computed data)
  getRetryStatus(recordingId: string): RetryStatusUpdate | undefined {
    return this.retryStatus.get(recordingId);
  }

  // Update computed data for template (call this when data changes)
  private updateComputedData(): void {
    // Update retrying recording IDs array
    this.retryingRecordingIds = Array.from(this.retryingRecordings);

    // Update computed data for each recording
    this.recordingComputedData.clear();
    for (const recording of this.failedRecordings) {
      const isRetrying = this.retryingRecordings.has(recording.id);
      
      this.recordingComputedData.set(recording.id, {
        hasFinalResult: !!(recording.finalResult || this.retryResults.has(recording.id)),
        isRetrying: isRetrying,
        retryStatus: this.retryStatus.get(recording.id),
        patientName: recording.patientName || 'Unknown Patient', // Pre-compute for status display
        outcome: this.describeOutcome(recording)
      });
    }
  }

  // Get computed data for a recording
  getRecordingComputedData(recordingId: string) {
    return this.recordingComputedData.get(recordingId) || {
      hasFinalResult: false,
      isRetrying: false,
      patientName: 'Recording',
      outcome: RECORDING_OUTCOMES.unfinished
    };
  }

  /**
   * Says what actually happened to a recording, in the clinician's terms.
   *
   * The distinction this draws is the point of the whole `abandoned` status. Until it existed
   * every row here was labelled a failure, because a failure was the only thing this list could
   * represent — and a consultation whose tab simply died, with every segment uploaded and
   * transcribed perfectly, could not be represented at all and so was shown nowhere. Calling
   * that a failure would be both wrong and alarming: nothing failed, the audio is intact, and
   * it is one click from becoming a note.
   */
  private describeOutcome(recording: FailedRecording): RecordingOutcome {
    if (recording.finalResult || this.retryResults.has(recording.id)) {
      return RECORDING_OUTCOMES.ready;
    }
    if (recording.status === 'abandoned') {
      return RECORDING_OUTCOMES.unfinished;
    }
    if (recording.transcriptionFailed || recording.uploadFailed || recording.status === 'failed') {
      return RECORDING_OUTCOMES.failed;
    }
    return RECORDING_OUTCOMES.unfinished;
  }

  // Check if recording has completed retry and has final result (kept for backward compatibility, but prefer computed data)
  hasFinalResult(recording: FailedRecording): boolean {
    return !!(recording.finalResult || this.retryResults.has(recording.id));
  }

  /**
   * ── WARD-ROUND HAND-OFF, KEPT IN STEP WITH `ai-dialog` ───────────────────────────────────
   *
   * `tasks/risk-management.md` D1 changed what a ward-round completion CARRIES, and this file
   * consumes it. Two things moved under it:
   *
   *  1. `finalResult` USED TO BE THE NOTES STRING AND IS NOW A SERIALIZED `AIRequest`.
   *     `GenerateWardRoundOutput.sendCompletion` sends `request: requestJson` where it used to
   *     send the bare notes text, and `RecordingRetryService` stores that verbatim as
   *     `recording.finalResult`. `viewResult` then put it on the note as `notes: finalResult` —
   *     which now writes an OBJECT into a string form control, i.e. `[object Object]` into the
   *     doctor's clinical note. That is a live data-corruption path, not a theoretical one, and
   *     it is why the notes text is now unwrapped through `resolveWardRoundRequest` exactly as
   *     `ai-dialog.component.ts` does.
   *
   *  2. WARD ROUNDS NOW HAVE A `requestId`. Before D1 they had none, so `if (!recording.requestId)
   *     return` aborted every ward round that reached it and the `edit-request` fallback below it
   *     could not run at all. Both now run. The re-fetch path is an IMPROVEMENT — a ward round
   *     with no stored `finalResult` used to dead-end on "request ID not found" and now recovers
   *     its notes from the row. The `edit-request` fallback stays as it is: it fires only when the
   *     request has no notes AND no transcript, which is a genuinely broken request, and sending
   *     the user to the request screen is a better dead end than a note with no body. (It is a
   *     one-way trip — B4 hides ward rounds from the patient-file request list — which is
   *     acceptable for a failure state and is called out here so it is not mistaken for a bug.)
   *
   * The note built here now carries `recordingJobId`, `aiRequestId` and `suggestedRiskItems`, the
   * same three fields `ai-dialog`'s close path attaches. Without them a ward round recovered
   * through this dialog would silently lose its provenance and skip workstream F entirely — the
   * doctor would never be offered the facility issues the extractor found.
   */

  /**
   * The notes text and provenance for a ward-round hand-off, from whatever shape reached us.
   *
   * Mirrors `ai-dialog.component.ts`'s `resolveWardRoundRequest`, including the re-fetch: the
   * id-only stub `{ id }` carries no notes, and saving an empty note is worse than the pre-D1
   * behaviour. Returns null when there is no request object to work from, in which case the
   * caller falls back to treating the raw result as the notes string.
   */
  private async resolveWardRoundRequest(finalResult: any, requestId?: string): Promise<any | null> {
    if (!finalResult || typeof finalResult !== 'object') {
      return null;
    }

    if (typeof finalResult.notes === 'string' && finalResult.notes.length > 0) {
      return finalResult;
    }

    const id = finalResult.id ?? requestId;
    if (!id) {
      return finalResult;
    }

    try {
      const response: any = await this.patientAiService.getPatientAIRequestById(id).toPromise();
      return response?.data ?? finalResult;
    } catch (error) {
      console.warn('[FailedRecordingsDialog] Failed to re-fetch ward round AI request:', error);
      return finalResult;
    }
  }

  /**
   * `AIRequest.suggestedRiskItems` as a flat array. Copy of `ai-dialog`'s helper: the column is
   * NULL — not `[]` — for every request predating the feature and every recording where nothing
   * was found, and anything malformed collapses to empty rather than being half-parsed, because
   * these end up on a doctor's letterhead.
   */
  private extractSuggestedRiskItems(request: any | null): any[] {
    const raw = request?.suggestedRiskItems;
    if (!raw) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw;
    }
    return Array.isArray(raw.items) ? raw.items : [];
  }

  /** The editing note for a ward-round hand-off. Same shape `ai-dialog`'s close path builds. */
  private buildWardRoundNote(request: any | null, fallbackNotes: any, recording: FailedRecording): any {
    const noteType = this.noteTypeService.getWardRoundNoteType();
    const notesFromRequest = typeof request?.notes === 'string' ? request.notes : null;
    const notesFromFallback = typeof fallbackNotes === 'string' ? fallbackNotes : '';

    return {
      title: 'Ward Round',
      // Never an object. See the header — this is the corruption D1 introduced here.
      notes: notesFromRequest ?? notesFromFallback,
      detectedNoteType: noteType,
      // `id` and `jobId` are written equal by RecordingStorageService; `jobId` is the field that
      // means this, so it is preferred and `id` is the fallback.
      recordingJobId: recording.jobId ?? recording.id ?? null,
      aiRequestId: request?.id ?? recording.requestId ?? null,
      suggestedRiskItems: this.extractSuggestedRiskItems(request)
    };
  }

  /** Navigate to add-note for a ward round, same route ai-dialog uses. */
  private navigateToWardRoundNote(patientId: string): void {
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['add-note']
      }
    }], {
      queryParams: {
        patientId: patientId,
      }
    });
  }

  // View completed result - loads AI request the same way ai-dialog does
  // For ward-rounds, behaves the same as ai-dialog's closeDialog() method
  async viewResult(recording: FailedRecording) {
    const isWardRound = recording.type === 'ward-round';
    
    // Get finalResult from recording or retryResults (matching ai-dialog pattern)
    const finalResult = recording.finalResult || this.retryResults.get(recording.id);
    
    // For ward-rounds, we can use the finalResult directly if available
    if (isWardRound && finalResult) {
      // Delete the recording from IndexedDB now that user has viewed it
      this.recordingStorageService.deleteRecording(recording.id).catch(err =>
        console.warn(`[FailedRecordingsDialog] Failed to delete recording:`, err)
      );

      // Remove from local arrays to update UI immediately
      this.failedRecordings = this.failedRecordings.filter(r => r.id !== recording.id);
      this.retryResults.delete(recording.id);
      this.updateComputedData();

      // Set up note with ward round note type (same as ai-dialog closeDialog).
      //
      // `finalResult` is a serialized AIRequest since D1, NOT the notes string it used to be, so
      // it is unwrapped rather than assigned. Passed as the fallback too, for the older
      // GenerateWardRoundOutput that still sends a bare string.
      const wardRoundRequest = await this.resolveWardRoundRequest(finalResult, recording.requestId);
      this.patientNoteService.setEditingNote(this.buildWardRoundNote(wardRoundRequest, finalResult, recording));

      // Navigate to add-note route (same as ai-dialog for ward-rounds)
      this.navigateToWardRoundNote(recording.patientId);

      this.dialogRef.close(null);
      return;
    }

    // For general notes or ward-rounds without finalResult, load AI request
    if (!recording.requestId) {
      console.error('[FailedRecordingsDialog] No requestId found for recording:', recording.id);
      this.feedbackService.showToast('Unable to load AI request: request ID not found');
      return;
    }

    try {
      // Load the AI request by ID (same as patient-ai-request-list component)
      this.patientAiService.getPatientAIRequestById(recording.requestId).subscribe({
        next: (response) => {
          const aiRequest = response?.data;
          if (!aiRequest) {
            console.error('[FailedRecordingsDialog] AI request not found:', recording.requestId);
            this.feedbackService.showToast('AI request not found');
            return;
          }

          // Delete the recording from IndexedDB now that user has viewed it
          this.recordingStorageService.deleteRecording(recording.id).catch(err =>
            console.warn(`[FailedRecordingsDialog] Failed to delete recording:`, err)
          );

          // Remove from local arrays to update UI immediately
          this.failedRecordings = this.failedRecordings.filter(r => r.id !== recording.id);
          this.retryResults.delete(recording.id);
          this.updateComputedData();

          // For ward-rounds without finalResult, check if we can extract it from aiRequest
          if (isWardRound) {
            // Try to get notes from aiRequest (could be in notes field or request field).
            //
            // STRING-ONLY, since D1: `finalResult` is a serialized AIRequest object on this path
            // too, and an object is truthy. Left as-is, an empty request would pass the
            // `if (notesContent)` test on the strength of an object that cannot be note text,
            // skip the edit-request fallback below, and hand the doctor a blank ward-round note.
            const asNotesText = (value: any) => (typeof value === 'string' && value.length > 0 ? value : null);
            const notesContent = asNotesText(aiRequest.notes) ?? asNotesText(aiRequest.request) ?? asNotesText(finalResult);

            if (notesContent) {
              // Set up note with ward round note type (same as ai-dialog closeDialog).
              //
              // `aiRequest` is the full row, so `buildWardRoundNote` takes its notes, its id and
              // its `suggestedRiskItems` (F). The fallback is the pre-existing precedence chain
              // above — transcript, then whatever `finalResult` held — and it is only reached if
              // `aiRequest.notes` is empty, in which case `notesContent` is a string.
              this.patientNoteService.setEditingNote(
                this.buildWardRoundNote(aiRequest, notesContent, recording)
              );

              // Navigate to add-note route (same as ai-dialog for ward-rounds)
              this.navigateToWardRoundNote(recording.patientId);
            } else {
              // Fallback: navigate to edit-request if notes not available
              console.warn('[FailedRecordingsDialog] No notes content found for ward-round, navigating to edit-request');
              this.router.navigate(['/patients', {
                outlets: {
                  patient: ['edit-request', aiRequest.id]
                }
              }]);
            }
          } else {
            // Navigate to edit-request route (same as patient-ai-request-list component)
            this.router.navigate(['/patients', {
              outlets: {
                patient: ['edit-request', aiRequest.id]
              }
            }]);
          }
          
          this.dialogRef.close(null);
        },
        error: (error) => {
          console.error('[FailedRecordingsDialog] Error loading AI request:', error);
          this.feedbackService.showToast('Error loading AI request');
        }
      });
    } catch (error) {
      console.error('[FailedRecordingsDialog] Error in viewResult:', error);
      this.feedbackService.showToast('Error loading AI request');
    }
  }

  // Get patient name for status display (computed)
  getPatientNameForStatus(recordingId: string): string {
    const recording = this.failedRecordings.find(r => r.id === recordingId);
    return recording?.patientName || 'Recording';
  }
}
